/* Rejoya 3D layer: orbiting product stage, pointer tilt on cards and depth reveal on scroll. */
(() => {
  if (window.__rj3d) return;
  window.__rj3d = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const wrap = (deg) => ((((deg + 180) % 360) + 360) % 360) - 180;

  /* ---------- Orbit stage (sections/rj-hero-3d.liquid) ---------- */
  class RjOrbit extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.ring = this.querySelector('[data-ring]');
      this.items = [...this.querySelectorAll('[data-item]')];
      if (!this.stage || !this.ring || !this.items.length) return;

      this.step = 360 / this.items.length;
      this.angle = 0;
      this.velocity = 0;
      this.target = null;
      this.speed = Number(this.dataset.speed || 10);
      this.tilt = -10;
      this.tiltGoal = -10;
      this.cam = { x: 50, y: 32, gx: 50, gy: 32 };
      this.frontIndex = -1;
      this.now = {
        link: this.querySelector('[data-now]'),
        name: this.querySelector('[data-now-name]'),
        price: this.querySelector('[data-now-price]'),
      };
      this.frame = this.frame.bind(this);

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.stage);
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible) this.start();
      });
      this.visibilityObserver.observe(this);
      this.onVisibility = () => !document.hidden && this.start();
      document.addEventListener('visibilitychange', this.onVisibility);

      this.bindDrag();
      this.bindPointer();
      this.bindControls();
      this.measure();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    measure() {
      const width = this.stage.clientWidth;
      const mobile = width < 560;
      this.radius = Math.round(Math.min(width * (mobile ? 0.34 : 0.33), 330));
      this.style.setProperty('--r', `${this.radius}px`);
      this.render();
    }

    start() {
      if (this.raf || !this.items.length) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.frame);
    }

    frame(time) {
      this.raf = 0;
      const dt = Math.min(64, time - this.last) / 1000;
      this.last = time;

      if (!this.dragging) {
        if (this.target !== null) {
          const delta = this.target - this.angle;
          this.angle += delta * Math.min(1, dt * 7);
          if (Math.abs(delta) < 0.05) {
            this.angle = this.target;
            this.target = null;
            this.pauseUntil = time + 2600;
          }
        } else if (Math.abs(this.velocity) > 1) {
          this.angle += this.velocity * dt;
          this.velocity *= Math.pow(0.05, dt);
        } else if (!reduceMotion.matches && !(this.pauseUntil > time)) {
          this.angle -= this.speed * (this.hovering ? 0.3 : 1) * dt;
        }
      }

      const ease = Math.min(1, dt * 5);
      this.tilt += (this.tiltGoal - this.tilt) * ease;
      this.cam.x += (this.cam.gx - this.cam.x) * ease;
      this.cam.y += (this.cam.gy - this.cam.y) * ease;
      this.render();

      const settling =
        Math.abs(this.tiltGoal - this.tilt) > 0.05 || Math.abs(this.cam.gx - this.cam.x) > 0.05 || this.target !== null;
      const spinning = !reduceMotion.matches && this.speed > 0;
      if (this.visible && !document.hidden && (spinning || settling || this.dragging || Math.abs(this.velocity) > 1)) {
        this.raf = requestAnimationFrame(this.frame);
      }
    }

    render() {
      const r = this.radius || 260;
      this.stage.style.perspectiveOrigin = `${this.cam.x.toFixed(2)}% ${this.cam.y.toFixed(2)}%`;
      this.ring.style.transform = `translateZ(${-r}px) rotateX(${this.tilt.toFixed(2)}deg)`;

      let front = 0;
      let frontZ = -Infinity;
      this.items.forEach((item, index) => {
        const rad = ((index * this.step + this.angle) * Math.PI) / 180;
        const x = Math.sin(rad) * r;
        const z = Math.cos(rad) * r;
        const turn = Math.sin(rad) * 16;
        item.style.transform = `translate3d(${x.toFixed(1)}px, 0, ${z.toFixed(1)}px) rotateY(${turn.toFixed(2)}deg) rotateX(${(-this.tilt).toFixed(2)}deg)`;
        item.style.opacity = (0.5 + ((z / r + 1) / 2) * 0.5).toFixed(3);
        if (z > frontZ) {
          frontZ = z;
          front = index;
        }
      });
      if (front !== this.frontIndex) this.setFront(front);
    }

    setFront(index) {
      this.items[this.frontIndex]?.classList.remove('is-front');
      this.frontIndex = index;
      const item = this.items[index];
      item.classList.add('is-front');
      if (this.now.name) this.now.name.textContent = item.dataset.title || '';
      if (this.now.price) this.now.price.textContent = item.dataset.price || '';
      if (this.now.link) this.now.link.href = item.getAttribute('href');
    }

    goTo(angle) {
      this.velocity = 0;
      this.target = angle;
      this.start();
    }

    bindControls() {
      const snap = (dir) => Math.round(this.angle / this.step) * this.step + dir * this.step;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(snap(1)));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(snap(-1)));
      this.items.forEach((item, index) => {
        item.addEventListener('focus', () => {
          if (item.matches(':focus-visible')) this.goTo(this.angle + wrap(-index * this.step - this.angle));
        });
      });
    }

    bindDrag() {
      let lastX = 0;
      let lastT = 0;
      let moved = 0;
      let pointerId = null;

      this.stage.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.target.closest('button')) return;
        pointerId = event.pointerId;
        this.dragging = true;
        this.target = null;
        this.velocity = 0;
        moved = 0;
        lastX = event.clientX;
        lastT = performance.now();
        this.start();
      });

      this.stage.addEventListener('pointermove', (event) => {
        if (!this.dragging || event.pointerId !== pointerId) return;
        const dx = event.clientX - lastX;
        const now = performance.now();
        moved += Math.abs(dx);
        if (moved > 6 && !this.stage.hasPointerCapture(pointerId)) {
          this.stage.setPointerCapture(pointerId);
          this.stage.classList.add('is-dragging');
        }
        this.angle += dx * 0.32;
        this.velocity = ((dx * 0.32) / Math.max(8, now - lastT)) * 1000;
        lastX = event.clientX;
        lastT = now;
        this.start();
      });

      const end = (event) => {
        if (!this.dragging || event.pointerId !== pointerId) return;
        this.dragging = false;
        this.suppressClick = moved > 6;
        this.stage.classList.remove('is-dragging');
        if (performance.now() - lastT > 80) this.velocity = 0;
        this.pauseUntil = performance.now() + 1800;
        this.start();
      };
      this.stage.addEventListener('pointerup', end);
      this.stage.addEventListener('pointercancel', end);
      this.stage.addEventListener(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          this.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }

    bindPointer() {
      this.stage.addEventListener('pointerenter', () => (this.hovering = true));
      this.stage.addEventListener('pointerleave', () => (this.hovering = false));
      if (!finePointer.matches) return;

      this.addEventListener('pointermove', (event) => {
        const box = this.getBoundingClientRect();
        const px = (event.clientX - box.left) / box.width - 0.5;
        const py = (event.clientY - box.top) / box.height - 0.5;
        this.tiltGoal = -10 - py * 10;
        this.cam.gx = 50 - px * 34;
        this.cam.gy = 32 + py * 16;
        this.style.setProperty('--px', px.toFixed(3));
        this.style.setProperty('--py', py.toFixed(3));
        this.start();
      });

      this.addEventListener('pointerleave', () => {
        this.tiltGoal = -10;
        this.cam.gx = 50;
        this.cam.gy = 32;
        this.style.setProperty('--px', '0');
        this.style.setProperty('--py', '0');
        this.start();
      });
    }
  }

  if (!customElements.get('rj-orbit')) customElements.define('rj-orbit', RjOrbit);

  /* ---------- Pointer tilt for product cards, tiles and offer cards ---------- */
  const TILT = '.product-card .card-gallery, .rj-tile__media, .rj-step, .rj-editorial__media, .product-information media-gallery .product-media-container';
  let tiltEl = null;
  let tiltEvent = null;
  let tiltRaf = 0;

  const clearTilt = (el) => {
    el.style.transform = '';
    el.classList.remove('is-tilting');
  };

  const applyTilt = () => {
    tiltRaf = 0;
    if (!tiltEl || !tiltEvent) return;
    const box = tiltEl.getBoundingClientRect();
    const px = Math.min(1, Math.max(0, (tiltEvent.clientX - box.left) / box.width));
    const py = Math.min(1, Math.max(0, (tiltEvent.clientY - box.top) / box.height));
    const soft = tiltEl.matches('.product-media-container');
    const max = soft ? 4 : tiltEl.matches('.rj-step') ? 7 : 10;
    const lift = soft ? '' : ' scale3d(1.025, 1.025, 1.025)';
    tiltEl.style.transform = `perspective(900px) rotateX(${((0.5 - py) * max).toFixed(2)}deg) rotateY(${((px - 0.5) * max).toFixed(2)}deg)${lift}`;
    tiltEl.style.setProperty('--gx', `${(px * 100).toFixed(1)}%`);
    tiltEl.style.setProperty('--gy', `${(py * 100).toFixed(1)}%`);
  };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType !== 'mouse' || reduceMotion.matches) return;
      let el = event.target instanceof Element ? event.target.closest(TILT) : null;
      if (el && el.closest('dialog')) el = null;
      if (el !== tiltEl) {
        if (tiltEl) clearTilt(tiltEl);
        tiltEl = el;
        if (el) el.classList.add('is-tilting');
      }
      tiltEvent = event;
      if (el && !tiltRaf) tiltRaf = requestAnimationFrame(applyTilt);
    },
    { passive: true }
  );

  document.documentElement.addEventListener('mouseleave', () => {
    if (tiltEl) clearTilt(tiltEl);
    tiltEl = null;
  });

  /* ---------- Depth reveal on scroll ---------- */
  const REVEAL = [
    'main .shopify-section:not(:first-child) .section-resource-list__header',
    'main .shopify-section:not(:first-child) .resource-list__item',
    '.rj-tile',
    '.rj-steps__intro',
    '.rj-step',
    '.rj-editorial__media',
    '.rj-editorial__copy',
    '.rj-benefit',
    '.rj-reveal',
  ].join(',');

  if (!reduceMotion.matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          observer.unobserve(el);
          el.classList.add('is-in');
          const delay = parseFloat(el.style.getPropertyValue('--rv-d')) || 0;
          setTimeout(() => {
            el.classList.remove('rj-rv', 'is-in');
            el.style.removeProperty('--rv-d');
          }, 1300 + delay);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
    );

    const scan = (root) => {
      root.querySelectorAll(REVEAL).forEach((el) => {
        if (el.dataset.rjRv) return;
        el.dataset.rjRv = '1';
        const box = el.getBoundingClientRect();
        if (box.top < window.innerHeight && box.bottom > 0) return;
        const order = el.parentElement ? Array.prototype.indexOf.call(el.parentElement.children, el) : 0;
        el.style.setProperty('--rv-d', `${Math.min(order, 5) * 80}ms`);
        el.classList.add('rj-rv');
        observer.observe(el);
      });
    };

    const boot = () => scan(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  }
})();